/**
 * @file videoPlayer.component.tsx
 * @description Industry-standard video player synced with Sir Bastians theme colors.
 * Handles dynamic playback rate and theme-compliant UI controls.
 */

import React, { useEffect } from "react";
import { cn } from "@sirbastians/utils";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
} from "lucide-react";
import { useVideoPlayer } from "@/hooks/useVideoPlayer.hook";

interface VideoPlayerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  poster?: string;
  showControls?: boolean;
  className?: string;
  playbackRate?: number; // ✨ FIXED: Type-safe prop for video speed
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  poster,
  showControls = true,
  className,
  playbackRate = 1, // Default to normal speed
  ...props
}) => {
  const { refs, state, actions, utils } = useVideoPlayer();

  /**
   * ✨ DOM Sync: playbackRate is a DOM property, not a standard HTML attribute.
   * We use a side-effect to ensure the video element stays in sync with React state.
   */
  useEffect(() => {
    if (refs.videoRef.current) {
      refs.videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, refs.videoRef]);

  return (
    <div
      ref={refs.containerRef}
      className={cn(
        "group relative flex w-full overflow-hidden bg-nav-bg shadow-lg",
        className,
      )}
    >
      <video
        ref={refs.videoRef}
        src={src}
        poster={poster}
        className="h-full w-full object-cover"
        onClick={showControls ? actions.togglePlay : undefined}
        onTimeUpdate={actions.handleTimeUpdate}
        onLoadedMetadata={actions.handleLoadedMetadata}
        {...props}
      />

      {/* Play Overlay - Uses 'accent' for brand consistency */}
      {showControls && !state.isPlaying && (
        <button
          onClick={actions.togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-nav-bg/40 text-nav-text transition-opacity hover:bg-nav-bg/50"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/90 backdrop-blur-sm">
            <Play className="h-8 w-8 ml-1" fill="currentColor" />
          </div>
        </button>
      )}

      {/* Control Bar - Strictly uses nav theme variables */}
      {showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-nav-bg/80 to-transparent px-4 py-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          {/* Progress Bar */}
          <div className="group/progress relative mb-4 h-1.5 w-full cursor-pointer rounded-full bg-nav-text/30">
            <input
              type="range"
              min="0"
              max="100"
              value={state.progress || 0}
              onChange={actions.handleSeek}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              aria-label="Seek progress"
            />
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-accent transition-all"
              style={{ width: `${state.progress}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-nav-text">
            <div className="flex items-center gap-4">
              <button
                onClick={actions.togglePlay}
                className="transition-colors hover:text-accent"
              >
                {state.isPlaying ? (
                  <Pause className="h-5 w-5" fill="currentColor" />
                ) : (
                  <Play className="h-5 w-5" fill="currentColor" />
                )}
              </button>

              <div className="group/volume flex items-center gap-2">
                <button
                  onClick={actions.toggleMute}
                  className="transition-colors hover:text-accent"
                >
                  {state.isMuted || state.volume === 0 ? (
                    <VolumeX className="h-5 w-5" />
                  ) : (
                    <Volume2 className="h-5 w-5" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={state.isMuted ? 0 : state.volume}
                  onChange={actions.handleVolumeChange}
                  className="w-0 cursor-pointer opacity-0 transition-all duration-300 group-hover/volume:w-20 group-hover/volume:opacity-100 accent-accent"
                  aria-label="Volume"
                />
              </div>

              <div className="text-xs font-medium text-nav-text-muted">
                {utils.formatTime(state.currentTime)} /{" "}
                {utils.formatTime(state.duration)}
              </div>
            </div>

            <button
              onClick={actions.toggleFullscreen}
              className="transition-colors hover:text-accent"
            >
              {state.isFullscreen ? (
                <Minimize className="h-5 w-5" />
              ) : (
                <Maximize className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
